/**
 * Adaptador para compatibilidade entre User antigo (nome) e novo (name)
 */

import type { User } from '../types';

/**
 * Gera um slug a partir de um texto
 */
function generateSlugFromText(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD') // Decompor caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remover acentos
    .replace(/[^a-z0-9]+/g, '-') // Substituir não-alfanuméricos por hífen
    .replace(/(^-|-$)/g, ''); // Remover hífens do início/fim
}

/**
 * Normaliza o usuário da API (PascalCase) para o formato do frontend (camelCase)
 */
export function normalizeUser(apiUser: any): User {
  return {
    // camelCase (formato frontend)
    id: apiUser.Id || apiUser.id,
    name: apiUser.Name || apiUser.name || apiUser.nome,
    email: apiUser.Email || apiUser.email,
    phoneNumber: apiUser.PhoneNumber || apiUser.phoneNumber,
    lastLoginAt: apiUser.LastLoginAt || apiUser.lastLoginAt || apiUser.ultimoAcesso,
    createdAt: apiUser.CreatedAt || apiUser.createdAt || apiUser.dataCriacao,
    isActive: apiUser.IsActive !== undefined ? apiUser.IsActive : (apiUser.isActive !== undefined ? apiUser.isActive : apiUser.ativo),
    avatarUrl: apiUser.AvatarUrl || apiUser.avatarUrl,
    roles: apiUser.roles,
    // Mantém campos originais para compatibilidade
    ...apiUser
  };
}

/**
 * Obtém o nome do usuário de forma compatível com ambos os formatos
 */
export function getUserName(user: User | null | undefined): string {
  if (!user) return 'Usuário';
  // Compatibilidade: tenta 'name' primeiro (novo formato), depois 'nome' (antigo), depois PascalCase
  return (user as any).name || (user as any).Name || (user as any).nome || user.email?.split('@')[0] || (user as any).Email?.split('@')[0] || 'Usuário';
}

/**
 * Normaliza dados de curso que podem vir em PascalCase ou camelCase
 */
export function normalizeCurso(apiCurso: any): any {
  if (!apiCurso) return null;

  const titulo = apiCurso.Title || apiCurso.title || apiCurso.titulo;
  
  return {
    id: apiCurso.Id || apiCurso.id,
    title: titulo,
    slug: apiCurso.Slug || apiCurso.slug || generateSlugFromText(titulo),
    description: apiCurso.Description || apiCurso.description || apiCurso.descricao,
    level: apiCurso.Level || apiCurso.level || apiCurso.nivel,
    durationMinutes: apiCurso.DurationMinutes ?? apiCurso.durationMinutes ?? apiCurso.duracao,
    ordem: apiCurso.Ordem ?? apiCurso.ordem,
    capa: apiCurso.Capa || apiCurso.capa,
    progresso: apiCurso.progresso,
    concluido: apiCurso.concluido,
  };
}

/**
 * Normaliza dados de trilha que podem vir em PascalCase ou camelCase
 */
export function normalizeTrilha(apiTrilha: any): any {
  if (!apiTrilha) return null;

  return {
    id: apiTrilha.Id || apiTrilha.id,
    nome: apiTrilha.Nome || apiTrilha.nome,
    descricao: apiTrilha.Descricao || apiTrilha.descricao,
    ordem: apiTrilha.Ordem ?? apiTrilha.ordem,
    ativo: apiTrilha.Ativo ?? apiTrilha.ativo,
    dataCriacao: apiTrilha.DataCriacao || apiTrilha.dataCriacao,
    cursos: (apiTrilha.Cursos || apiTrilha.cursos || []).map(normalizeCurso),
    totalCursos: apiTrilha.TotalCursos ?? apiTrilha.totalCursos ?? 0,
    duracaoTotalMinutos: apiTrilha.DuracaoTotalMinutos ?? apiTrilha.duracaoTotalMinutos ?? 0,
    capaUrl: apiTrilha.Thumbnail || apiTrilha.capaUrl,
    progresso: apiTrilha.progresso,
  };
}

/**
 * Converte CreateUserDTO do formato antigo para o novo
 */
export function adaptCreateUserDTO(oldFormat: {
  nome: string;
  email: string;
  senha: string;
  roles?: string[];
  ativo?: boolean;
}): {
  name: string;
  email: string;
  password: string;
  phoneNumber?: string;
} {
  return {
    name: oldFormat.nome,
    email: oldFormat.email,
    password: oldFormat.senha,
  };
}

/**
 * Converte UpdateUserDTO do formato antigo para o novo
 */
export function adaptUpdateUserDTO(oldFormat: {
  nome?: string;
  email?: string;
  senha?: string;
  roles?: string[];
  ativo?: boolean;
}): {
  name?: string;
  email?: string;
  phoneNumber?: string;
} {
  return {
    name: oldFormat.nome,
    email: oldFormat.email,
  };
}

/**
 * Normaliza usuário ADMIN (inclui avatarUrl padrão e roles)
 */
export function normalizeUserAdmin(apiUser: any): any {
  if (!apiUser) return null;
  
  return {
    id: apiUser.Id || apiUser.id,
    name: apiUser.Name || apiUser.name || apiUser.nome,
    nome: apiUser.Name || apiUser.name || apiUser.nome,
    email: apiUser.Email || apiUser.email,
    phoneNumber: apiUser.PhoneNumber || apiUser.phoneNumber,
    isActive: apiUser.IsActive ?? apiUser.isActive ?? apiUser.ativo ?? true,
    ativo: apiUser.IsActive ?? apiUser.isActive ?? apiUser.ativo ?? true,
    lastLoginAt: apiUser.LastLoginAt || apiUser.lastLoginAt || apiUser.ultimoAcesso,
    ultimoAcesso: apiUser.LastLoginAt || apiUser.lastLoginAt || apiUser.ultimoAcesso,
    createdAt: apiUser.CreatedAt || apiUser.createdAt || apiUser.dataCriacao,
    dataCriacao: apiUser.CreatedAt || apiUser.createdAt || apiUser.dataCriacao,
    avatarUrl: apiUser.AvatarUrl || apiUser.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(apiUser.Name || apiUser.name || apiUser.nome || 'User')}`,
    roles: (apiUser.Roles || apiUser.roles || []).map((r: string) => r.toLowerCase()), // Normalizar para lowercase
  };
}

/**
 * Normaliza curso ADMIN
 */
export function normalizeCursoAdmin(apiCurso: any): any {
  if (!apiCurso) return null;
  
  const titulo = apiCurso.Title || apiCurso.title || apiCurso.titulo;
  
  return {
    id: apiCurso.Id || apiCurso.id,
    title: titulo,
    titulo: titulo,
    slug: apiCurso.Slug || apiCurso.slug || generateSlugFromText(titulo),
    description: apiCurso.Description || apiCurso.description || apiCurso.descricao,
    descricao: apiCurso.Description || apiCurso.description || apiCurso.descricao,
    level: apiCurso.Level || apiCurso.level || apiCurso.nivel,
    nivel: apiCurso.Level || apiCurso.level || apiCurso.nivel,
    durationMinutes: apiCurso.DurationMinutes ?? apiCurso.durationMinutes ?? apiCurso.duracaoMinutos,
    duracaoMinutos: apiCurso.DurationMinutes ?? apiCurso.durationMinutes ?? apiCurso.duracaoMinutos,
    category: apiCurso.Category || apiCurso.category || apiCurso.categoria,
    categoria: apiCurso.Category || apiCurso.category || apiCurso.categoria,
    duracao: apiCurso.Duracao || apiCurso.duracao,
    instrutor: apiCurso.Instrutor || apiCurso.instrutor || apiCurso.InstructorName || apiCurso.instructorName,
    isActive: apiCurso.IsActive ?? apiCurso.isActive ?? apiCurso.ativo ?? true,
    ativo: apiCurso.IsActive ?? apiCurso.isActive ?? apiCurso.ativo ?? true,
    instructorId: apiCurso.InstructorId || apiCurso.instructorId,
    instructorName: apiCurso.InstructorName || apiCurso.instructorName,
    lessonsCount: apiCurso.LessonsCount ?? apiCurso.lessonsCount ?? 0,
    price: apiCurso.Price ?? apiCurso.price ?? 0,
    createdAt: apiCurso.CreatedAt || apiCurso.createdAt,
    capaUrl: apiCurso.CapaUrl || apiCurso.capaUrl || 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=400',
  };
}

/**
 * Normaliza trilha ADMIN
 */
export function normalizeTrilhaAdmin(apiTrilha: any): any {
  if (!apiTrilha) return null;

  return {
    id: apiTrilha.Id || apiTrilha.id,
    nome: apiTrilha.Nome || apiTrilha.nome || apiTrilha.name,
    name: apiTrilha.Nome || apiTrilha.nome || apiTrilha.name,
    descricao: apiTrilha.Descricao || apiTrilha.descricao || apiTrilha.description,
    description: apiTrilha.Descricao || apiTrilha.descricao || apiTrilha.description,
    ordem: apiTrilha.Ordem ?? apiTrilha.ordem,
    ativo: apiTrilha.Ativo ?? apiTrilha.ativo ?? apiTrilha.isActive ?? apiTrilha.IsActive ?? true,
    isActive: apiTrilha.Ativo ?? apiTrilha.ativo ?? apiTrilha.isActive ?? apiTrilha.IsActive ?? true,
    dataCriacao: apiTrilha.DataCriacao || apiTrilha.dataCriacao || apiTrilha.createdAt || apiTrilha.CreatedAt,
    createdAt: apiTrilha.DataCriacao || apiTrilha.dataCriacao || apiTrilha.createdAt || apiTrilha.CreatedAt,
    cursos: (apiTrilha.Cursos || apiTrilha.cursos || []).map(normalizeCurso),
    totalCursos: apiTrilha.TotalCursos ?? apiTrilha.totalCursos ?? apiTrilha.Cursos?.length ?? apiTrilha.cursos?.length ?? 0,
    duracaoTotalMinutos: apiTrilha.DuracaoTotalMinutos ?? apiTrilha.duracaoTotalMinutos ?? 0,
    capaUrl: apiTrilha.Thumbnail || apiTrilha.capaUrl || apiTrilha.CapaUrl || 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400',
    progresso: apiTrilha.progresso,
  };
}

/**
 * Normaliza empresa/cliente ADMIN
 * FIX-001: Corrigida normalização para corresponder à estrutura real da API
 */
export function normalizeEmpresaAdmin(apiEmpresa: any): any {
  console.log('🔄 [normalizeEmpresaAdmin] Normalizando empresa:', apiEmpresa);
  
  if (!apiEmpresa) {
    console.warn('⚠️ [normalizeEmpresaAdmin] Empresa nula ou undefined');
    return null;
  }

  const normalized = {
    id: apiEmpresa.Id || apiEmpresa.id,
    // FIX: API retorna Name (às vezes vazio) - fallback para CNPJ ou 'Sem nome'
    nome: apiEmpresa.Name || apiEmpresa.Nome || apiEmpresa.nome || apiEmpresa.name || apiEmpresa.CNPJ || apiEmpresa.cnpj || 'Sem nome',
    cnpj: apiEmpresa.CNPJ || apiEmpresa.Cnpj || apiEmpresa.cnpj,
    email: apiEmpresa.Email || apiEmpresa.email,
    telefone: apiEmpresa.Telefone || apiEmpresa.telefone,
    endereco: apiEmpresa.Endereco || apiEmpresa.endereco,
    responsavel: apiEmpresa.Responsavel || apiEmpresa.responsavel,
    // FIX: API retorna IsActive (PascalCase)
    ativo: apiEmpresa.IsActive ?? apiEmpresa.Ativo ?? apiEmpresa.ativo ?? apiEmpresa.isActive ?? true,
    isActive: apiEmpresa.IsActive ?? apiEmpresa.Ativo ?? apiEmpresa.ativo ?? apiEmpresa.isActive ?? true,
    // FIX: TotalCursos ainda não existe na API (aguardando backend)
    totalCursos: apiEmpresa.TotalCursos ?? apiEmpresa.totalCursos ?? 0,
    // FIX: API retorna TotalTrilhas (PascalCase) - correto
    totalTrilhas: apiEmpresa.TotalTrilhas ?? apiEmpresa.totalTrilhas ?? 0,
    // FIX: API retorna TotalUsers (não TotalUsuarios)
    totalUsuarios: apiEmpresa.TotalUsers ?? apiEmpresa.TotalUsuarios ?? apiEmpresa.totalUsuarios ?? apiEmpresa.totalUsers ?? 0,
    // FIX: API retorna CreatedAt (PascalCase)
    dataCriacao: apiEmpresa.CreatedAt || apiEmpresa.DataCriacao || apiEmpresa.dataCriacao || apiEmpresa.createdAt,
    createdAt: apiEmpresa.CreatedAt || apiEmpresa.DataCriacao || apiEmpresa.dataCriacao || apiEmpresa.createdAt,
  };

  console.log('✅ [normalizeEmpresaAdmin] Empresa normalizada:', normalized);
  console.log('📊 [normalizeEmpresaAdmin] Detalhes:', {
    'Nome escolhido': normalized.nome,
    'Name da API': apiEmpresa.Name,
    'CNPJ da API': apiEmpresa.CNPJ,
    'TotalUsers da API': apiEmpresa.TotalUsers,
    'TotalTrilhas da API': apiEmpresa.TotalTrilhas,
    'TotalCursos da API': apiEmpresa.TotalCursos,
    'totalUsuarios normalizado': normalized.totalUsuarios,
    'totalTrilhas normalizado': normalized.totalTrilhas,
    'totalCursos normalizado': normalized.totalCursos,
  });

  return normalized;
}

/**
 * Normaliza grupo de trilhas ADMIN
 */
export function normalizeGrupoAdmin(apiGrupo: any): any {
  if (!apiGrupo) return null;

  return {
    id: apiGrupo.Id || apiGrupo.id,
    empresaId: apiGrupo.EmpresaId || apiGrupo.empresaId,
    nome: apiGrupo.Nome || apiGrupo.nome || apiGrupo.name,
    descricao: apiGrupo.Descricao || apiGrupo.descricao || apiGrupo.description,
    cor: apiGrupo.Cor || apiGrupo.cor || apiGrupo.color,
    ordem: apiGrupo.Ordem ?? apiGrupo.ordem ?? apiGrupo.order ?? 0,
    totalTrilhas: apiGrupo.TotalTrilhas ?? apiGrupo.totalTrilhas ?? 0,
    totalUsuarios: apiGrupo.TotalUsuarios ?? apiGrupo.totalUsuarios ?? 0,
    dataCriacao: apiGrupo.DataCriacao || apiGrupo.dataCriacao || apiGrupo.CreatedAt || apiGrupo.createdAt,
    createdAt: apiGrupo.DataCriacao || apiGrupo.dataCriacao || apiGrupo.CreatedAt || apiGrupo.createdAt,
  };
}

/**
 * Normaliza trilha de grupo (com customizações)
 */
export function normalizeGrupoTrilha(apiGrupoTrilha: any): any {
  if (!apiGrupoTrilha) return null;

  return {
    id: apiGrupoTrilha.Id || apiGrupoTrilha.id,
    grupoId: apiGrupoTrilha.GrupoId || apiGrupoTrilha.grupoId,
    trilhaId: apiGrupoTrilha.TrilhaId || apiGrupoTrilha.trilhaId,
    trilha: apiGrupoTrilha.Trilha ? normalizeTrilhaAdmin(apiGrupoTrilha.Trilha) : apiGrupoTrilha.trilha,
    displayOrder: apiGrupoTrilha.DisplayOrder ?? apiGrupoTrilha.displayOrder ?? 0,
    isRequired: apiGrupoTrilha.IsRequired ?? apiGrupoTrilha.isRequired ?? false,
    customName: apiGrupoTrilha.CustomName || apiGrupoTrilha.customName,
    customDescription: apiGrupoTrilha.CustomDescription || apiGrupoTrilha.customDescription,
  };
}

/**
 * Normaliza usuário de grupo
 */
export function normalizeGrupoUsuario(apiGrupoUsuario: any): any {
  if (!apiGrupoUsuario) return null;

  return {
    id: apiGrupoUsuario.Id || apiGrupoUsuario.id,
    grupoId: apiGrupoUsuario.GrupoId || apiGrupoUsuario.grupoId,
    userId: apiGrupoUsuario.UserId || apiGrupoUsuario.userId,
    user: apiGrupoUsuario.User ? normalizeUserAdmin(apiGrupoUsuario.User) : apiGrupoUsuario.user,
    joinedAt: apiGrupoUsuario.JoinedAt || apiGrupoUsuario.joinedAt,
  };
}

/**
 * Normaliza lesson ADMIN (Painel Instrutor)
 */
export function normalizeLessonAdmin(apiLesson: any): any {
  if (!apiLesson) return null;

  return {
    id: apiLesson.Id || apiLesson.id,
    courseId: apiLesson.CourseId || apiLesson.courseId,
    title: apiLesson.Title || apiLesson.title || apiLesson.titulo,
    description: apiLesson.Description || apiLesson.description || apiLesson.descricao,
    order: apiLesson.Order ?? apiLesson.order ?? apiLesson.ordem ?? 0,
    durationSeconds: apiLesson.DurationSeconds ?? apiLesson.durationSeconds ?? 0,
    videoId: apiLesson.VideoId || apiLesson.videoId,
    video: apiLesson.Video || apiLesson.video,
    youtubeUrl: apiLesson.YoutubeUrl || apiLesson.youtubeUrl,
    youtubeVideoId: apiLesson.YoutubeVideoId || apiLesson.youtubeVideoId,
    videoType: apiLesson.VideoType || apiLesson.videoType || 'Upload',
    isActive: apiLesson.IsActive ?? apiLesson.isActive ?? apiLesson.ativo ?? true,
    ativo: apiLesson.IsActive ?? apiLesson.isActive ?? apiLesson.ativo ?? true,
    createdAt: apiLesson.CreatedAt || apiLesson.createdAt,
    updatedAt: apiLesson.UpdatedAt || apiLesson.updatedAt,
  };
}

